/**
 * What counts in a cart. The cart page and the topbar's cart count both use these,
 * so the two always show the same number.
 *
 * Kept free of other app imports, so ducks and helpers can use it without import cycles.
 */
import { LISTING_STATE_CLOSED } from './types';

const stockOf = listing => listing?.currentStock?.attributes?.quantity;

/** A listing that can still be bought: not closed, and with stock left. */
export const isCartListingPurchasable = listing => {
  const stock = stockOf(listing);
  return (
    !!listing &&
    listing.attributes?.state !== LISTING_STATE_CLOSED &&
    Number.isInteger(stock) &&
    stock > 0
  );
};

/** The buyer's own listing: it can't be bought by them, so it isn't in their cart. */
export const isOwnCartListing = (listing, currentUserId) =>
  !!currentUserId && listing?.author?.id?.uuid === currentUserId;

/**
 * How many of a listing the buyer wants: their pick, at least 1 and at most the stock.
 * 0 for a listing that can't be bought.
 */
export const cartQuantityOf = (listing, quantities) => {
  if (!isCartListingPurchasable(listing)) return 0;
  const picked = quantities?.[listing.id?.uuid];
  const wanted = Number.isInteger(picked) && picked > 0 ? picked : 1;
  return Math.min(wanted, stockOf(listing));
};

/**
 * The number of items in a cart: the units of every listing that can be bought,
 * leaving out the buyer's own listings.
 */
export const cartUnitCount = (listings, quantities, currentUserId) =>
  (listings || []).reduce(
    (sum, listing) =>
      isOwnCartListing(listing, currentUserId) ? sum : sum + cartQuantityOf(listing, quantities),
    0
  );
